import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, LogOut, RefreshCw, Mail, PencilRuler, CreditCard, Wallet,
  Smartphone, Landmark, LayoutDashboard, DollarSign, Users, Loader2,
  Download, CheckCircle2, Circle,
} from "lucide-react";
import { WebIcon } from "../../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ew_admin_token";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const when = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function downloadCSV(filename, rows) {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => {
    const s = v == null ? "" : Array.isArray(v) ? v.join(" | ") : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const PROVIDER_META = {
  stripe: { label: "Stripe", icon: CreditCard },
  paypal: { label: "PayPal", icon: Wallet },
  venmo: { label: "Venmo", icon: Smartphone },
  zelle: { label: "Zelle", icon: Landmark },
};

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center px-6" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-10">
          <WebIcon className="w-16 h-16" stroke="#87CEEB" />
          <h1 className="font-serif text-4xl text-white mt-4">Elevated</h1>
          <span className="font-display text-xs font-semibold tracking-[0.5em] text-sky-brand">WEB · ADMIN</span>
        </div>
        <form onSubmit={submit} className="bg-white/[0.04] border border-white/10 rounded-[2rem] p-8 backdrop-blur-xl">
          <div className="flex items-center gap-2 text-sky-brand mb-6">
            <Lock size={18} />
            <span className="font-display text-sm uppercase tracking-widest font-semibold">Secure Login</span>
          </div>
          <label className="font-display text-sm text-white/70 block mb-2">Email</label>
          <input
            data-testid="admin-email-input"
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="w-full bg-white/5 border border-white/15 focus:border-sky-brand outline-none px-5 py-3.5 rounded-2xl font-body text-white placeholder:text-white/30 mb-5"
            placeholder="you@email.com"
          />
          <label className="font-display text-sm text-white/70 block mb-2">Password</label>
          <input
            data-testid="admin-password-input"
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="w-full bg-white/5 border border-white/15 focus:border-sky-brand outline-none px-5 py-3.5 rounded-2xl font-body text-white placeholder:text-white/30 mb-7"
            placeholder="••••••••"
          />
          <button
            data-testid="admin-login-button"
            type="submit" disabled={loading}
            className="font-display w-full bg-sky-brand text-charcoal hover:bg-white transition-colors font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Log in"}
          </button>
        </form>
        <p className="text-center text-white/30 font-body text-xs mt-6">Authorized access only.</p>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
    <div className="flex items-center justify-between">
      <span className="font-display text-sm text-gray-500">{label}</span>
      <div className="w-9 h-9 rounded-xl bg-sky-brand/15 flex items-center justify-center"><Icon size={18} className="text-sky-brand" /></div>
    </div>
    <div className="font-display text-3xl font-bold text-charcoal mt-3">{value}</div>
  </div>
);

const Badge = ({ status }) => {
  const paid = status === "paid" || status === "complete";
  return (
    <span className={`font-display text-[0.65rem] uppercase tracking-wide px-2.5 py-1 rounded-full ${paid ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
      {status || "—"}
    </span>
  );
};

export default function AdminDashboard() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [edits, setEdits] = useState([]);
  const [editRequests, setEditRequests] = useState([]);
  const [payments, setPayments] = useState({ stripe: [], paypal: [], manual: [], all: [] });
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const timer = useRef(null);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  }, []);

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [s, c, e, p, er] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeader()),
        axios.get(`${API}/admin/contacts`, authHeader()),
        axios.get(`${API}/admin/site-edits`, authHeader()),
        axios.get(`${API}/admin/payments`, authHeader()),
        axios.get(`${API}/admin/edit-requests`, authHeader()),
      ]);
      setStats(s.data); setContacts(c.data); setEdits(e.data); setPayments(p.data); setEditRequests(er.data);
      setLastUpdated(new Date());
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Session expired — please log in again");
        logout();
      }
    } finally {
      setRefreshing(false);
    }
  }, [logout]);

  const toggleFlag = async (type, id, key, value) => {
    try {
      await axios.post(`${API}/admin/flag`, { type, id, key, value }, authHeader());
      if (type === "contact") setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, [key]: value } : c)));
      if (type === "site_edit") setEdits((prev) => prev.map((e) => (e.id === id ? { ...e, [key]: value } : e)));
      if (type === "manual_payment") setPayments((prev) => ({ ...prev, manual: prev.manual.map((m) => (m.id === id ? { ...m, [key]: value } : m)) }));
      toast.success(value ? "Marked" : "Unmarked");
    } catch {
      toast.error("Could not update — try again");
    }
  };

  const approveEditRequest = async (id) => {
    try {
      await axios.post(`${API}/admin/edit-requests/${id}/approve`, {}, authHeader());
      setEditRequests((prev) => prev.map((r) => (r.id === id ? { ...r, approved: true, outcome: "logged", handled: true } : r)));
      toast.success("Approved — added to their plan (free) and client notified");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not approve — try again");
    }
  };

  useEffect(() => {
    if (!authed) return;
    loadAll();
    timer.current = setInterval(() => loadAll(true), 10000); // auto-refresh ~real time
    return () => clearInterval(timer.current);
  }, [authed, loadAll]);

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "contacts", label: `Enquiries (${contacts.length})`, icon: Mail },
    { id: "edits", label: `Site Edits (${edits.length})`, icon: PencilRuler },
    { id: "edit-requests", label: `Edit Requests (${editRequests.length})`, icon: CheckCircle2 },
    { id: "payments", label: `Payments (${payments.all.length + payments.manual.length})`, icon: DollarSign },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-charcoal text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <WebIcon className="w-8 h-8" stroke="#87CEEB" />
            <div className="leading-none">
              <div className="font-serif text-xl">Elevated</div>
              <div className="font-display text-[0.6rem] tracking-[0.4em] text-sky-brand">WEB · ADMIN</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="admin-refresh" onClick={() => loadAll()} className="text-white/70 hover:text-sky-brand transition-colors inline-flex items-center gap-2 font-display text-sm">
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="bg-white/10 hover:bg-white/20 transition-colors font-display text-sm px-4 py-2 rounded-full inline-flex items-center gap-2">
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`font-display text-sm font-medium px-5 py-2.5 rounded-full inline-flex items-center gap-2 transition-colors ${
                tab === t.id ? "bg-charcoal text-white" : "bg-white text-gray-600 hover:text-charcoal border border-gray-200"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {lastUpdated && (
          <p className="font-body text-xs text-gray-400 mb-6" data-testid="admin-last-updated">
            Live · auto-refreshing every 10s · last updated {lastUpdated.toLocaleTimeString()}
          </p>
        )}

        {/* Overview */}
        {tab === "overview" && stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-overview">
            <StatCard icon={Mail} label="Enquiries" value={stats.contacts} />
            <StatCard icon={PencilRuler} label="Site Edit Requests" value={stats.site_edits} />
            <StatCard icon={Users} label="Payment Attempts" value={stats.payments_total} />
            <StatCard icon={DollarSign} label="Revenue (paid)" value={money(stats.revenue_paid)} />
          </div>
        )}

        {/* Enquiries */}
        {tab === "contacts" && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="admin-contacts">
            <SectionHeader
              label="Enquiries"
              onExport={() => downloadCSV("enquiries.csv", contacts.map((c) => ({
                name: c.name, business: c.business_name, email: c.email, phone: c.phone,
                message: c.message, handled: c.handled ? "yes" : "no", date: c.created_at,
              })))}
            />
            {contacts.length === 0 ? <Empty text="No enquiries yet." /> : (
              <div className="divide-y divide-gray-100">
                {contacts.map((c) => (
                  <div key={c.id} className={`p-5 hover:bg-gray-50 ${c.handled ? "opacity-60" : ""}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-display font-semibold text-charcoal">{c.name} <span className="text-gray-400 font-normal">· {c.business_name || "—"}</span></div>
                        <div className="font-body text-sm text-gray-500 mt-1">{c.email} · {c.phone}</div>
                        <p className="font-body text-charcoal mt-3">{c.message}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="font-body text-xs text-gray-400 whitespace-nowrap">{when(c.created_at)}</span>
                        <ToggleBtn active={!!c.handled} testid={`contact-handled-${c.id}`}
                          onClick={() => toggleFlag("contact", c.id, "handled", !c.handled)}
                          labelOn="Handled" labelOff="Mark handled" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Site Edits */}
        {tab === "edits" && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="admin-edits">
            <SectionHeader
              label="Site Edit Requests"
              onExport={() => downloadCSV("site-edits.csv", edits.map((e) => ({
                business: e.business_name, website: e.website_url, requests: e.requests,
                request_count: e.request_count, estimated_cost: e.estimated_cost,
                wants_call: e.schedule_call ? "yes" : "no", handled: e.handled ? "yes" : "no", date: e.created_at,
              })))}
            />
            {edits.length === 0 ? <Empty text="No site edit requests yet." /> : (
              <div className="divide-y divide-gray-100">
                {edits.map((e) => (
                  <div key={e.id} className={`p-5 hover:bg-gray-50 ${e.handled ? "opacity-60" : ""}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="font-display font-semibold text-charcoal">{e.business_name} <span className="text-gray-400 font-normal">· {e.website_url}</span></div>
                        <ol className="mt-3 space-y-1.5">
                          {e.requests.map((r, i) => (
                            <li key={i} className="font-body text-sm text-charcoal flex gap-2">
                              <span className="text-sky-brand font-semibold">{i + 1}.</span> {r}
                            </li>
                          ))}
                        </ol>
                        <div className="mt-3 flex items-center gap-3 font-body text-sm">
                          <span className="text-gray-500">{e.request_count} request{e.request_count !== 1 ? "s" : ""}</span>
                          <span className="font-display font-bold text-sky-brand">{money(e.estimated_cost)} est.</span>
                          {e.schedule_call && <span className="text-[0.65rem] uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">wants a call</span>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="font-body text-xs text-gray-400 whitespace-nowrap">{when(e.created_at)}</span>
                        <ToggleBtn active={!!e.handled} testid={`edit-handled-${e.id}`}
                          onClick={() => toggleFlag("site_edit", e.id, "handled", !e.handled)}
                          labelOn="Handled" labelOff="Mark handled" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Edit Requests (Airtable-verified /edits flow) */}
        {tab === "edit-requests" && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="admin-edit-requests">
            <SectionHeader
              label="Edit Requests"
              onExport={() => downloadCSV("edit-requests.csv", editRequests.map((r) => ({
                name: r.name, email: r.email, website: r.website_url, requests: r.requests,
                request_count: r.request_count, plan_active: r.plan_active ? "yes" : "no",
                outcome: r.outcome, approved: r.approved ? "yes" : "no", date: r.created_at,
              })))}
            />
            {editRequests.length === 0 ? <Empty text="No edit requests yet." /> : (
              <div className="divide-y divide-gray-100">
                {editRequests.map((r) => {
                  const needsApproval = r.plan_active && !r.approved;
                  return (
                    <div key={r.id} className={`p-5 hover:bg-gray-50 ${r.approved || r.outcome === "logged" ? "opacity-60" : ""}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="font-display font-semibold text-charcoal">
                            {r.name} <span className="text-gray-400 font-normal">· {r.email}</span>
                          </div>
                          {r.website_url && <div className="font-body text-xs text-gray-400 mt-0.5">{r.website_url}</div>}
                          <ol className="mt-3 space-y-1.5">
                            {r.requests.map((req, i) => (
                              <li key={i} className="font-body text-sm text-charcoal flex gap-2">
                                <span className="text-sky-brand font-semibold">{i + 1}.</span> {req}
                              </li>
                            ))}
                          </ol>
                          <div className="mt-3 flex items-center gap-2 flex-wrap">
                            {r.plan_active ? (
                              <span className="text-[0.65rem] uppercase bg-green-100 text-green-700 px-2 py-0.5 rounded-full">active plan</span>
                            ) : (
                              <span className="text-[0.65rem] uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">no plan · $50/req</span>
                            )}
                            <span className={`text-[0.65rem] uppercase px-2 py-0.5 rounded-full ${
                              r.outcome === "logged" ? "bg-green-100 text-green-700"
                              : r.outcome === "pending_approval" ? "bg-blue-100 text-blue-700"
                              : "bg-gray-100 text-gray-500"}`}>
                              {(r.outcome || "").replace(/_/g, " ")}
                            </span>
                            <span className="font-body text-sm text-gray-500">{r.request_count} request{r.request_count !== 1 ? "s" : ""}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="font-body text-xs text-gray-400 whitespace-nowrap">{when(r.created_at)}</span>
                          {needsApproval ? (
                            <button
                              data-testid={`edit-request-approve-${r.id}`}
                              onClick={() => approveEditRequest(r.id)}
                              className="font-display text-xs font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 bg-sky-brand text-charcoal hover:bg-charcoal hover:text-white transition-colors"
                            >
                              <CheckCircle2 size={14} /> Approve (free)
                            </button>
                          ) : r.approved || r.outcome === "logged" ? (
                            <span className="font-display text-xs font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 bg-green-100 text-green-700">
                              <CheckCircle2 size={14} /> Approved
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Payments */}
        {tab === "payments" && (
          <div className="space-y-8" data-testid="admin-payments">
            <div className="flex justify-end">
              <ExportBtn onClick={() => downloadCSV("payments.csv", [
                ...payments.stripe.map((p) => ({ provider: "stripe", customer: p.business_name, email: p.email, service: p.service_type, amount: p.amount, status: p.payment_status, date: p.created_at })),
                ...payments.paypal.map((p) => ({ provider: "paypal", customer: p.business_name, email: p.email, service: p.service_type, amount: p.amount, status: p.payment_status, date: p.created_at })),
                ...payments.manual.map((m) => ({ provider: m.method, customer: m.business_name, email: m.email, service: m.service_type, amount: m.amount, status: m.paid ? "paid" : "claimed", date: m.created_at })),
              ])} />
            </div>
            <PaymentTable title="Stripe (Card / Apple Pay)" icon={CreditCard} rows={payments.stripe} kind="txn" />
            <PaymentTable title="PayPal" icon={Wallet} rows={payments.paypal} kind="txn" />
            <PaymentTable title="Venmo / Zelle (manual)" icon={Smartphone} rows={payments.manual} kind="manual"
              onToggle={(id, val) => toggleFlag("manual_payment", id, "paid", val)} />
          </div>
        )}
      </div>
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="p-12 text-center font-body text-gray-400">{text}</div>
);

const ToggleBtn = ({ active, onClick, labelOn, labelOff, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`font-display text-xs font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
      active ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
    }`}
  >
    {active ? <CheckCircle2 size={14} /> : <Circle size={14} />}
    {active ? labelOn : labelOff}
  </button>
);

const ExportBtn = ({ onClick }) => (
  <button
    data-testid="admin-export-csv"
    onClick={onClick}
    className="font-display text-sm font-medium px-4 py-2 rounded-full inline-flex items-center gap-2 bg-charcoal text-white hover:bg-sky-brand hover:text-charcoal transition-colors"
  >
    <Download size={15} /> Export CSV
  </button>
);

const SectionHeader = ({ label, onExport }) => (
  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
    <h3 className="font-display font-semibold text-charcoal">{label}</h3>
    <ExportBtn onClick={onExport} />
  </div>
);

function PaymentTable({ title, icon: Icon, rows, kind, onToggle }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
        <Icon size={18} className="text-sky-brand" />
        <h3 className="font-display font-semibold text-charcoal">{title}</h3>
        <span className="font-body text-sm text-gray-400">({rows.length})</span>
      </div>
      {rows.length === 0 ? <Empty text="Nothing here yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="font-display text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="px-5 py-3">Customer</th>
                <th className="px-5 py-3">Service</th>
                <th className="px-5 py-3">Amount</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Date</th>
                {kind === "manual" && <th className="px-5 py-3">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r, i) => (
                <tr key={r.id || i} className="hover:bg-gray-50 font-body text-sm">
                  <td className="px-5 py-3 text-charcoal">{r.business_name || "—"}<div className="text-gray-400 text-xs">{r.email || (kind === "manual" ? r.confirmation_message : "")}</div></td>
                  <td className="px-5 py-3 text-gray-600">{(r.service_type || "").replace(/_/g, " ")}{kind === "manual" && <span className="ml-1 text-gray-400">· {r.method}</span>}</td>
                  <td className="px-5 py-3 font-display font-semibold text-charcoal">{money(r.amount)}</td>
                  <td className="px-5 py-3">{kind === "manual"
                    ? <span className={`font-display text-[0.65rem] uppercase px-2.5 py-1 rounded-full ${r.paid ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>{r.paid ? "paid" : "claimed"}</span>
                    : <Badge status={r.payment_status} />}</td>
                  <td className="px-5 py-3 text-gray-400 whitespace-nowrap text-xs">{when(r.created_at)}</td>
                  {kind === "manual" && (
                    <td className="px-5 py-3">
                      <ToggleBtn active={!!r.paid} testid={`manual-paid-${r.id}`}
                        onClick={() => onToggle(r.id, !r.paid)}
                        labelOn="Paid" labelOff="Mark paid" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
